import type { Patient, Reference } from '@medplum/fhirtypes'
import { MemoNote } from '@seen/core/models/memoNote'
import { useCallback, useRef } from 'react'
import { useCreateMutation } from '#src/hooks/useCreateMutation.ts'
import { useCurrentPractitioner } from '#src/hooks/useCurrentPractitioner.ts'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'
import { useSearchOne } from '#src/hooks/useSearchOne.ts'

export function useMemoNote(participant: Reference<Patient>) {
  const practitioner = useCurrentPractitioner()
  const isReadOnly = useIsReadOnly()
  const isCreatingRef = useRef(false)

  const { data: memo, isLoading } = useSearchOne(
    MemoNote,
    MemoNote.queryByParticipant(participant)
  )

  const { mutateAsync: createMemoNote } = useCreateMutation<MemoNote>()

  const createMemo = useCallback(async () => {
    if (isCreatingRef.current || isReadOnly) {
      return
    }
    isCreatingRef.current = true
    try {
      await createMemoNote(
        new MemoNote({
          participant,
          author: practitioner.reference,
        })
      )
    } finally {
      isCreatingRef.current = false
    }
  }, [createMemoNote, isReadOnly, participant, practitioner.reference])

  const needsCreation = !isLoading && memo === null

  return {
    memo,
    isLoading,
    needsCreation,
    createMemo,
  }
}
